// src/pages/ProductListPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, SlidersHorizontal, ChevronDown, Grid, List, Star, Heart, ShoppingCart } from 'lucide-react';
import { useCart } from './context/CartContext';
import { productAPI } from './services/api';
import { translateCategory, getCategoryIcon } from './utils/categoryTranslation';
import Header from './components/Header';

const ProductListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToCart } = useCart();
  const [viewMode, setViewMode] = useState('grid');
  
  // 從 URL 獲取初始分類
  const urlCategory = searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState(urlCategory);
  
  // 狀態管理
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // 保存所有產品用於計數
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxPrice, setMaxPrice] = useState(500000); // 動態最大價格
  const [priceRange, setPriceRange] = useState([0, 500000]); // 增加默認上限
  const [sortBy, setSortBy] = useState('popular');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [selectedBrands, setSelectedBrands] = useState([]);

  // 載入分類列表
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await productAPI.getCategories();
        setCategories(result.categories || []);
      } catch (error) {
        console.error('載入分類失敗:', error);
      }
    };
    fetchCategories();
  }, []);

  // 初始載入所有產品（用於計數和動態設置價格範圍）
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const result = await productAPI.getAllProducts({});
        setAllProducts(result.products || []);
        
        // 計算最大價格
        let calculatedMaxPrice = 10000;
        (result.products || []).forEach(p => {
          const price = parsePrice(p.price);
          const maxPriceValue = Math.max(price.min, price.max);
          if (maxPriceValue > calculatedMaxPrice) {
            calculatedMaxPrice = maxPriceValue;
          }
        });
        
        // 四捨五入到最接近的 10000
        calculatedMaxPrice = Math.ceil(calculatedMaxPrice / 10000) * 10000;
        setMaxPrice(calculatedMaxPrice);
        setPriceRange([0, calculatedMaxPrice]);
        
      } catch (error) {
        console.error('載入所有產品失敗:', error);
      }
    };
    fetchAllProducts();
  }, []);

  // 載入商品列表（根據選中的分類）
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const params = {};
        if (selectedCategory !== 'all') {
          params.category = selectedCategory;
        }
        const result = await productAPI.getAllProducts(params);
        setProducts(result.products || []);
        
        // 提取品牌列表
        const brandSet = new Set();
        (result.products || []).forEach(p => {
          if (p.brand) brandSet.add(p.brand);
        });
        setBrands(Array.from(brandSet).sort());
      } catch (error) {
        console.error('載入商品失敗:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  // 當 URL 參數變化時更新分類
  useEffect(() => {
    const newCategory = searchParams.get('category') || 'all';
    setSelectedCategory(newCategory);
  }, [searchParams]);

  /**
   * 解析價格字串，支持以下格式：
   * - 單一價格: "1500" -> { min: 1500, max: 1500 }
   * - 價格範圍: "1500~5000" -> { min: 1500, max: 5000 }
   * - 數字類型: 1500 -> { min: 1500, max: 1500 }
   */
  const parsePrice = (priceStr) => {
    if (typeof priceStr === 'number') {
      return { min: priceStr, max: priceStr };
    }
    
    if (typeof priceStr !== 'string') {
      return { min: 0, max: 0 };
    }

    // 移除可能的空格和貨幣符號
    const cleanPrice = priceStr.replace(/\s+/g, '').replace(/TWD|NT\$|\$/gi, '');
    
    // 檢查是否包含範圍符號 (~、-、to)
    if (cleanPrice.includes('~')) {
      const [minStr, maxStr] = cleanPrice.split('~');
      return {
        min: parseFloat(minStr) || 0,
        max: parseFloat(maxStr) || 0
      };
    } else if (cleanPrice.includes('-')) {
      const [minStr, maxStr] = cleanPrice.split('-');
      return {
        min: parseFloat(minStr) || 0,
        max: parseFloat(maxStr) || 0
      };
    } else if (cleanPrice.toLowerCase().includes('to')) {
      const [minStr, maxStr] = cleanPrice.toLowerCase().split('to');
      return {
        min: parseFloat(minStr) || 0,
        max: parseFloat(maxStr) || 0
      };
    }
    
    // 單一價格
    const price = parseFloat(cleanPrice) || 0;
    return { min: price, max: price };
  };

  // 篩選和排序商品
  const getFilteredAndSortedProducts = () => {
    let filtered = [...products];

    // 按品牌篩選
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }

    // 按價格篩選
    filtered = filtered.filter(product => {
      const price = parsePrice(product.price);
      const avgPrice = (price.min + price.max) / 2;
      return avgPrice >= priceRange[0] && avgPrice <= priceRange[1];
    });

    // 排序
    switch (sortBy) {
      case 'priceLowToHigh':
        filtered.sort((a, b) => {
          const priceA = parsePrice(a.price);
          const priceB = parsePrice(b.price);
          return (priceA.min + priceA.max) / 2 - (priceB.min + priceB.max) / 2;
        });
        break;
      case 'priceHighToLow':
        filtered.sort((a, b) => {
          const priceA = parsePrice(a.price);
          const priceB = parsePrice(b.price);
          return (priceB.min + priceB.max) / 2 - (priceA.min + priceA.max) / 2;
        });
        break;
      case 'rating':
        filtered.sort((a, b) => (parseFloat(b.rating) || 0) - (parseFloat(a.rating) || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => b.id - a.id);
        break;
      default: // popularity
        filtered.sort((a, b) => (parseInt(b.sold_count) || 0) - (parseInt(a.sold_count) || 0));
    }

    return filtered;
  };

  const filteredProducts = getFilteredAndSortedProducts();
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 處理分類切換
  const handleCategoryChange = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setCurrentPage(1);
    navigate(`/products${categoryKey !== 'all' ? `?category=${categoryKey}` : ''}`);
  };

  // 處理品牌篩選
  const toggleBrand = (brand) => {
    if (selectedBrands.includes(brand)) {
      setSelectedBrands(selectedBrands.filter(b => b !== brand));
    } else {
      setSelectedBrands([...selectedBrands, brand]);
    }
    setCurrentPage(1);
  };

  // 清除所有篩選
  const clearFilters = () => {
    setSelectedBrands([]);
    setPriceRange([0, maxPrice]);
    setCurrentPage(1);
  };

  // 格式化價格顯示
  const formatPriceDisplay = (priceStr) => {
    const price = parsePrice(priceStr);
    if (price.min === price.max) {
      return `TWD ${price.min.toLocaleString()}`;
    }
    return `TWD ${price.min.toLocaleString()} - ${price.max.toLocaleString()}`;
  };

  // 加入購物車
  const handleAddToCart = async (product) => {
    try {
      const productData = {
        productId: product.id,
        productName: product.name,
        variant: '默認規格',
        price: typeof product.price === 'string' ? parsePrice(product.price).min : product.price,
        originalPrice: product.original_price,
        quantity: 1,
        stock: product.stock || 100,
        imageUrl: product.image,
        sellerName: product.seller_name || '官方旗艦店'
      };

      await addToCart(productData);
      alert(t('success.addedToCart'));
    } catch (error) {
      alert(`❌ ${error.message}`);
    }
  };

  // 計算分類商品數量（使用 allProducts）
  const getCategoryCount = (categoryKey) => {
    if (categoryKey === 'all') return allProducts.length;
    return allProducts.filter(p => p.category === categoryKey).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 頂部導航 - 使用 product variant */}
      <Header 
        variant="product"
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      />

      {/* 主要內容 */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* 左側篩選欄 */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              {/* 篩選標題 */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <SlidersHorizontal size={20} />
                  {t('products.filters')}
                </h3>
                <button 
                  onClick={clearFilters}
                  className="text-sm text-orange-500 hover:text-orange-600"
                >
                  {t('products.clear')}
                </button>
              </div>

              {/* 分類篩選 */}
              <div className="mb-6 pb-6 border-b">
                <h4 className="font-semibold mb-3">{t('products.category')}</h4>
                <div className="space-y-2">
                  {/* 所有分類 */}
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className={`w-full text-left px-3 py-2 rounded transition ${
                      selectedCategory === 'all'
                        ? 'bg-orange-500 text-white'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center justify-between">
                      <span>{t('nav.allCategories')}</span>
                      <span className="text-sm">({allProducts.length})</span>
                    </span>
                  </button>
                  
                  {/* 動態分類 */}
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryChange(category)}
                      className={`w-full text-left px-3 py-2 rounded transition ${
                        selectedCategory === category
                          ? 'bg-orange-500 text-white'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        <span>
                          {getCategoryIcon(category)} {translateCategory(category, t)}
                        </span>
                        <span className="text-sm">({getCategoryCount(category)})</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 品牌篩選 */}
              {brands.length > 0 && (
                <div className="mb-6 pb-6 border-b">
                  <h4 className="font-semibold mb-3">{t('products.brand')}</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {brands.map((brand) => (
                      <label
                        key={brand}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={() => toggleBrand(brand)}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm">{brand}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 價格範圍 */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3">{t('products.priceRange')}</h4>
                <div className="space-y-4">
                  <div>
                    <input
                      type="range"
                      min="0"
                      max={maxPrice}
                      step="1000"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-600 mt-2">
                      <span>TWD 0</span>
                      <span>TWD {priceRange[1].toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-center">
                      最大價格: TWD {maxPrice.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 右側產品列表 */}
          <div className="flex-1">
            {/* 工具欄 */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">
                    {t('products.showing')} <span className="font-semibold">{filteredProducts.length}</span> {t('products.results')}
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  {/* 排序選擇 */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{t('products.sortBy')}:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="popularity">{t('products.popularity')}</option>
                      <option value="newest">{t('products.newest')}</option>
                      <option value="priceLowToHigh">{t('products.priceLowToHigh')}</option>
                      <option value="priceHighToLow">{t('products.priceHighToLow')}</option>
                      <option value="rating">{t('products.rating')}</option>
                    </select>
                  </div>

                  {/* 視圖切換 */}
                  <div className="flex gap-2 border rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      title={t('products.gridView')}
                    >
                      <Grid size={18} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      title={t('products.listView')}
                    >
                      <List size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 載入中 */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                <p className="mt-4 text-gray-600">{t('common.loading')}</p>
              </div>
            ) : paginatedProducts.length === 0 ? (
              /* 無商品 */
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-6xl mb-4">😢</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{t('products.noResults')}</h3>
                <p className="text-gray-600 mb-4">請嘗試調整篩選條件</p>
                <button
                  onClick={clearFilters}
                  className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600"
                >
                  {t('products.clear')}
                </button>
              </div>
            ) : (
              /* 產品網格/列表 */
              <>
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
                  : 'space-y-4'
                }>
                  {paginatedProducts.map((product) => (
                    <div
                      key={product.id}
                      className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group ${
                        viewMode === 'list' ? 'flex' : ''
                      }`}
                    >
                      {/* 商品圖片 */}
                      <div 
                        className={`bg-gray-100 overflow-hidden cursor-pointer ${
                          viewMode === 'list' ? 'w-48 h-48' : 'aspect-square'
                        }`}
                        onClick={() => navigate(`/product/${product.id}`)}
                      >
                        {product.image ? (
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <span className="text-6xl">📦</span>
                          </div>
                        )}
                      </div>

                      {/* 商品資訊 */}
                      <div className="p-4 flex-1">
                        <h3 
                          className="font-semibold text-gray-800 mb-2 line-clamp-2 cursor-pointer hover:text-orange-500 transition-colors"
                          onClick={() => navigate(`/product/${product.id}`)}
                        >
                          {product.name}
                        </h3>

                        {/* 評分 */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm text-gray-600 ml-1">
                              {(parseFloat(product.rating) || 0).toFixed(1)}
                            </span>
                          </div>
                          {product.reviews_count > 0 && (
                            <span className="text-sm text-gray-400">
                              ({product.reviews_count})
                            </span>
                          )}
                        </div>

                        {/* 價格 */}
                        <div className="mb-3">
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold text-orange-500">
                              {formatPriceDisplay(product.price)}
                            </span>
                            {product.original_price && parseFloat(product.original_price) > 0 && (
                              <span className="text-sm text-gray-400 line-through">
                                TWD {product.original_price}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* 操作按鈕 */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAddToCart(product)}
                            className="flex-1 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center justify-center gap-2"
                          >
                            <ShoppingCart size={16} />
                            {t('homepage.addToCart')}
                          </button>
                          <button
                            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            title={t('common.favorite')}
                          >
                            <Heart size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 分頁 */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.previous')}
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg ${
                          currentPage === page
                            ? 'bg-orange-500 text-white'
                            : 'border hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListPage;